/**
 * Floor-plan geometry, shared by the server (overlap checks) and the browser
 * (host stand and the layout editor). Positions are pixels on a plan; a table
 * with w and h of 1 or less has no explicit size and is drawn from its seats.
 */
export const LEGACY_CELL = 92;
export const SNAP = 12;

export interface BoxInput {
  maxSeats: number;
  shape?: string;
  w?: number | null;
  h?: number | null;
}

export function tableBox(t: BoxInput): { w: number; h: number } {
  if ((t.w ?? 0) > 1 && (t.h ?? 0) > 1) return { w: t.w as number, h: t.h as number };
  const s = t.maxSeats;
  if (t.shape === 'round') {
    const d = s >= 8 ? 112 : s >= 4 ? 84 : 58;
    return { w: d, h: d };
  }
  if (s <= 2) return { w: 60, h: 60 };
  if (s <= 4) return { w: 84, h: 60 };
  if (s <= 8) return { w: 150, h: 60 };
  return { w: 170, h: 70 };
}

export interface Placed extends BoxInput {
  id: string;
  x: number;
  y: number;
  /** Degrees clockwise about the centre. */
  rotation?: number | null;
}

interface Corners {
  id: string;
  pts: [number, number][];
}

function corners(t: Placed): Corners {
  const { w, h } = tableBox(t);
  const cx = t.x + w / 2;
  const cy = t.y + h / 2;
  const a = ((t.rotation ?? 0) * Math.PI) / 180;
  const cos = Math.cos(a);
  const sin = Math.sin(a);
  const pts = (
    [[-w / 2, -h / 2], [w / 2, -h / 2], [w / 2, h / 2], [-w / 2, h / 2]] as [number, number][]
  ).map(([dx, dy]) => [cx + dx * cos - dy * sin, cy + dx * sin + dy * cos] as [number, number]);
  return { id: t.id, pts };
}

/** Separating-axis test for two convex quadrilaterals. Touching edges do not overlap. */
function intersects(a: Corners, b: Corners): boolean {
  const EPS = 0.5;
  for (const poly of [a.pts, b.pts]) {
    for (let i = 0; i < 4; i += 1) {
      const [x1, y1] = poly[i];
      const [x2, y2] = poly[(i + 1) % 4];
      const nx = -(y2 - y1);
      const ny = x2 - x1;
      const project = (pts: [number, number][]) => {
        const v = pts.map(([x, y]) => x * nx + y * ny);
        return [Math.min(...v), Math.max(...v)] as const;
      };
      const [aMin, aMax] = project(a.pts);
      const [bMin, bMax] = project(b.pts);
      const len = Math.hypot(nx, ny) || 1;
      if (aMax - bMin <= EPS * len || bMax - aMin <= EPS * len) return false;
    }
  }
  return true;
}

/** Pairs of table ids whose (rotated) rectangles overlap. */
export function overlaps(tables: Placed[]): [string, string][] {
  const out: [string, string][] = [];
  const cs = tables.map(corners);
  for (let i = 0; i < cs.length; i += 1)
    for (let j = i + 1; j < cs.length; j += 1) {
      // Cheap reject on circumscribed circles before the exact test.
      if (intersects(cs[i], cs[j])) out.push([cs[i].id, cs[j].id]);
    }
  return out;
}
